import { create } from 'zustand';

// ── Types ──────────────────────────────────────────────────────

export interface ReportSummary {
  id: string;
  summary?: string;
  status: string;
  entityCount: number;
  relationshipCount: number;
  workspaceId?: string;
  sessionId?: string;
  userName?: string;
  messageCount: number;
  createdAt?: string;
}

export interface DraftEntity {
  id: string;
  name: string;
  type?: string;
  description?: string;
  status: string;
  ontologyClassification?: string;
  sortOrder: number;
}

export interface DraftRelationship {
  id: string;
  source: string;
  target: string;
  type: string;
  description?: string;
  status: string;
  sortOrder: number;
}

export interface ReportDetail {
  id: string;
  summary?: string;
  status: string;
  workspaceId?: string;
  sessionId?: string;
  userName?: string;
  userId?: string;
  messageCount: number;
  createdAt?: string;
  citations?: Record<string, string>;
  entities: DraftEntity[];
  relationships: DraftRelationship[];
}

export interface StewardStats {
  PENDING_REVIEW?: number;
  FINALIZED?: number;
  OUTDATED?: number;
  [key: string]: number | undefined;
}

interface StewardState {
  // Report list
  reports: ReportSummary[];
  loading: boolean;

  // Active report detail
  activeReport: ReportDetail | null;
  detailLoading: boolean;

  // Stats
  stats: StewardStats;
  statsLoading: boolean;

  error: string | null;

  // Actions
  loadReports: (workspaceId: string, token?: string | null, status?: string) => Promise<void>;
  loadReportDetail: (reportId: string, token?: string | null) => Promise<void>;
  loadStats: (workspaceId: string, token?: string | null) => Promise<void>;
  approveEntity: (entityId: string, token?: string | null) => Promise<void>;
  rejectEntity: (entityId: string, token?: string | null) => Promise<void>;
  finalizeReport: (reportId: string, reviewerName: string, comment: string, token?: string | null) => Promise<void>;
  markOutdated: (reportId: string, token?: string | null) => Promise<void>;
  clearActiveReport: () => void;
}

// ── Store ──────────────────────────────────────────────────────

const API_BASE = '/api/dotnet/steward';

export const useStewardStore = create<StewardState>()((set) => ({
  reports: [],
  loading: false,
  activeReport: null,
  detailLoading: false,
  stats: {},
  statsLoading: false,
  error: null,

  loadReports: async (workspaceId, token, status) => {
    set({ loading: true, error: null });
    try {
      const headers: HeadersInit = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;

      const filterStatus = status ?? 'PENDING_REVIEW';
      const url = `${API_BASE}/reports?workspaceId=${encodeURIComponent(workspaceId)}&status=${encodeURIComponent(filterStatus)}`;
      const res = await fetch(url, { headers });

      if (!res.ok) throw new Error(`Failed to load reports: ${res.status}`);

      const data: ReportSummary[] = await res.json();
      set({ reports: data, loading: false });
    } catch (err) {
      set({ error: (err as Error).message, loading: false });
    }
  },

  loadReportDetail: async (reportId, token) => {
    set({ detailLoading: true, error: null });
    try {
      const headers: HeadersInit = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;

      const res = await fetch(`${API_BASE}/reports/${encodeURIComponent(reportId)}`, { headers });

      if (!res.ok) throw new Error(`Failed to load report: ${res.status}`);

      const data: ReportDetail = await res.json();
      set({ activeReport: data, detailLoading: false });
    } catch (err) {
      set({ error: (err as Error).message, detailLoading: false });
    }
  },

  loadStats: async (workspaceId, token) => {
    set({ statsLoading: true });
    try {
      const headers: HeadersInit = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;

      const url = `${API_BASE}/stats?workspaceId=${encodeURIComponent(workspaceId)}`;
      const res = await fetch(url, { headers });

      if (!res.ok) throw new Error(`Failed to load stats: ${res.status}`);

      const data: StewardStats = await res.json();
      set({ stats: data, statsLoading: false });
    } catch {
      set({ statsLoading: false });
    }
  },

  approveEntity: async (entityId, token) => {
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;

    const res = await fetch(`${API_BASE}/entities/${encodeURIComponent(entityId)}/approve`, {
      method: 'POST',
      headers,
    });

    if (!res.ok) throw new Error(`Approve failed: ${res.status}`);

    // Update entity status locally in activeReport
    set((s) => {
      if (!s.activeReport) return s;
      return {
        activeReport: {
          ...s.activeReport,
          entities: s.activeReport.entities.map((e) =>
            e.id === entityId ? { ...e, status: 'APPROVED' } : e,
          ),
        },
      };
    });
  },

  rejectEntity: async (entityId, token) => {
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;

    const res = await fetch(`${API_BASE}/entities/${encodeURIComponent(entityId)}/reject`, {
      method: 'POST',
      headers,
    });

    if (!res.ok) throw new Error(`Reject failed: ${res.status}`);

    // Update entity status locally
    set((s) => {
      if (!s.activeReport) return s;
      return {
        activeReport: {
          ...s.activeReport,
          entities: s.activeReport.entities.map((e) =>
            e.id === entityId ? { ...e, status: 'REJECTED' } : e,
          ),
        },
      };
    });
  },

  finalizeReport: async (reportId, reviewerName, comment, token) => {
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;

    const res = await fetch(`${API_BASE}/reports/${encodeURIComponent(reportId)}/finalize`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ reviewerName, comment }),
    });

    if (!res.ok) throw new Error(`Finalize failed: ${res.status}`);

    // Remove from reports list and clear active
    set((s) => ({
      reports: s.reports.filter((r) => r.id !== reportId),
      activeReport: null,
    }));
  },

  markOutdated: async (reportId, token) => {
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;

    const res = await fetch(`${API_BASE}/reports/${encodeURIComponent(reportId)}/mark-outdated`, {
      method: 'POST',
      headers,
    });

    if (!res.ok) throw new Error(`Mark outdated failed: ${res.status}`);

    set((s) => ({
      reports: s.reports.filter((r) => r.id !== reportId),
      activeReport: null,
    }));
  },

  clearActiveReport: () => set({ activeReport: null }),
}));
